import React, { useState, useEffect } from "react";
import {
    Container,
    Button,
    Card,
    Row,
    Col,
    Breadcrumb
} from "react-bootstrap";
import { useParams, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { BsArrowLeft } from "react-icons/bs";
import Loader from "../../components/Loader";
import UserDetailsComponent from "./UserDetailsComponent";
import AppConstants from "../../AppConstants";

// Updated interface based on API response
interface APIUserDetails {
    id: string;
    email: string;
    firstName: string;
    lastName: string;
    fullName: string;
    phoneNumber: string;
    profilePicture?: string;
    profileImage?: string;
    registrationDate: string;
    lastLoginDate: string;
    status: string;
    isBlocked: boolean;
    subscription: {
        plan: string;
        maxUsers: number;
        currentUsers: number;
        status: string;
        currentPeriodStart: string;
        currentPeriodEnd: string;
    };
    subscriptions: Array<{
        stripeSubscriptionId: string;
        role: string;
        active: boolean;
        _id: string;
    }>;
    subscriptionRole: string | null;
    subscriptionPlan?: {
        priceId: string;
        unitAmount: number;
        currency: string;
        recurringInterval: string;
        productName: string;
    };
    paymentMethodDetails?: {
        id: string;
        type: string;
        brand: string;
        last4: string;
        exp_month: number;
        exp_year: number;
    };
    subscriptionOwnerName?: string;
    assignedUsers: Array<{
        memberId: string;
        firstName: string;
        lastName: string;
        email: string;
        active: boolean;
        role: string;
    }>;
    assignedUsersDetails: Array<{
        _id: string;
        firstName: string;
        lastName: string;
        email: string;
        subscriptions: Array<{
            stripeSubscriptionId: string;
            role: string;
            active: boolean;
            _id: string;
        }>;
    }>;
    inspectionCount: number;
    address?: {
        _id: string;
        line1: string;
        line2: string;
        city: string;
        state: string;
        zip: string;
    };
}

interface APIUserDetailsResponse {
    success: boolean;
    message: string;
    data: APIUserDetails;
}

// Payment History API interfaces
interface APIPaymentHistoryItem {
    id: string;
    amount: number;
    currency: string;
    status: string;
    created: string;
    description: string;
    subscriptionId: string;
    stripeInvoiceId: string;
    paymentMethodId: string;
    billingPeriod: {
        start: string;
        end: string;
    };
    maxUsers: number;
    statusColor: string;
    paymentMethod: {
        type: string;
        last4: string;
        brand: string;
    };
}

interface APIPaymentHistoryResponse {
    success: boolean;
    message: string;
    data: {
        payments: APIPaymentHistoryItem[];
        totalPayments: number;
        totalAmount: number;
        successfulPayments: number;
        formattedTotalAmount: string;
    };
}

// User interfaces for UserDetailsComponent
interface Address {
    street: string;
    city: string;
    state: string;
    zip: string;
}

interface CurrentPlan {
    name: string;
    duration: string;
    maxUsers: number;
    amount: string;
    paymentType: string;
    cardNumber: string;
}

interface PaymentHistory {
    id: string;
    date: string;
    endDate: string;
    amount: string;
    paymentMethod: string;
    maxUsers: number;
    discount?: string;
    status?: string;
}

interface AssignedUser {
    memberId: string;
    firstName: string;
    lastName: string;
    email: string;
    active: boolean;
    role: string;
}

interface User {
    id: string;
    name: string;
    accountType: "Owner" | "Member";
    email: string;
    phone: string;
    address?: Address;
    registeredOn: string;
    expiresOn: string;
    status: "Active" | "Inactive" | "Blocked";
    avatar: string;
    currentPlan?: CurrentPlan;
    assignedUsers?: AssignedUser[];
    paymentHistory?: PaymentHistory[];
    subscriptionOwnerName?: string; // Added for "Added by" functionality
}

// Helper function to format currency
const formatCurrency = (amount: number, currency: string): string => {
    const formatter = new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currency.toUpperCase(),
        minimumFractionDigits: 2,
    });
    return formatter.format(amount / 100); // Stripe amounts are in cents
};

// Helper function to format card brand
const formatCardBrand = (brand: string): string => {
    const brandMap: { [key: string]: string } = {
        'visa': 'Visa',
        'mastercard': 'MasterCard',
        'amex': 'American Express',
        'discover': 'Discover',
        'diners': 'Diners Club',
        'jcb': 'JCB',
        'unionpay': 'UnionPay'
    };
    return brandMap[brand.toLowerCase()] || brand.toUpperCase();
};

// Transform API data to match UserDetailsComponent interface
const transformUserData = (apiUser: APIUserDetails): User => {
    const userRole: "Owner" | "Member" = apiUser.subscriptionRole === 'owner' ? "Owner" : "Member";

    // Build current plan with proper amount and payment type for owners
    let currentPlan: CurrentPlan | undefined;
    
    // Use subscription object if available, otherwise create fallback for members with active subscriptions
    const activeSubscription = apiUser.subscriptions?.find(s => s.active);
    const subscription = apiUser.subscription || (activeSubscription ? {
        plan: userRole === "Owner" ? "Active Plan" : "Member Plan",
        currentPeriodStart: new Date().toISOString(), // fallback - current date
        currentPeriodEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(), // fallback - 30 days from now
        maxUsers: userRole === "Owner" ? 5 : 1, // fallback maxUsers
        currentUsers: 0, // fallback
        status: "active" // fallback
    } : null);

    if (subscription) {
        let amount = "N/A";
        let paymentType = "N/A";
        let cardNumber = "";
        
        // Only set amount and payment details for owners
        if (userRole === "Owner") {
            if (apiUser.subscriptionPlan) {
                amount = formatCurrency(apiUser.subscriptionPlan.unitAmount, apiUser.subscriptionPlan.currency);
            }
            
            if (apiUser.paymentMethodDetails) {
                paymentType = formatCardBrand(apiUser.paymentMethodDetails.brand);
                cardNumber = `****${apiUser.paymentMethodDetails.last4}`;
            }
        }

        currentPlan = {
            name: subscription.plan || "Active Plan",
            duration: `${new Date(subscription.currentPeriodStart).toLocaleDateString()} - ${new Date(subscription.currentPeriodEnd).toLocaleDateString()}`,
            maxUsers: subscription.maxUsers || 1,
            amount: amount,
            paymentType: paymentType,
            cardNumber: cardNumber
        };
    }

    return {
        id: apiUser.id,
        name: apiUser.fullName,
        accountType: userRole,
        email: apiUser.email,
        phone: apiUser.phoneNumber === "N/A" ? "" : apiUser.phoneNumber,
        address: apiUser.address ? {
            street: apiUser.address.line1 + (apiUser.address.line2 ? `, ${apiUser.address.line2}` : ''),
            city: apiUser.address.city,
            state: apiUser.address.state,
            zip: apiUser.address.zip
        } : undefined,
        registeredOn: new Date(apiUser.registrationDate).toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }),
        expiresOn: subscription ? new Date(subscription.currentPeriodEnd).toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }) : 'N/A',
        status: apiUser.isBlocked ? 'Blocked' : 
                apiUser.status === 'active' ? 'Active' : 'Inactive',
        avatar: apiUser.profilePicture || apiUser.profileImage || 
                `https://ui-avatars.com/api/?name=${encodeURIComponent(apiUser.fullName)}&background=random&color=fff&size=128`,
        currentPlan: currentPlan,
        assignedUsers: apiUser.assignedUsersDetails ? 
            apiUser.assignedUsersDetails.map(user => ({
                memberId: user._id,
                firstName: user.firstName,
                lastName: user.lastName,
                email: user.email,
                active: user.subscriptions?.[0]?.active ?? false,
                role: user.subscriptions?.[0]?.role ?? 'member'
            })) : 
            apiUser.assignedUsers || [],
        paymentHistory: [],
        subscriptionOwnerName: apiUser.subscriptionOwnerName 
    };
};

// Transform payment history API data to match PaymentHistory interface
const transformPaymentHistoryData = (apiPayments: APIPaymentHistoryItem[]): PaymentHistory[] => {
    return apiPayments.map(payment => ({
        id: payment.id,
        date: new Date(payment.created).toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }),
        endDate: new Date(payment.billingPeriod.end).toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit'
        }),
        amount: payment.amount === 0 ? 'Free' : `$${payment.amount.toFixed(2)}`,
        paymentMethod: payment.paymentMethod.type === 'card'
            ? `${payment.paymentMethod.brand.charAt(0).toUpperCase() + payment.paymentMethod.brand.slice(1)} **** ${payment.paymentMethod.last4}`
            : payment.paymentMethod.type,
        maxUsers: payment.maxUsers ?? 0,
        discount: payment.amount === 0 ? "Free Trial" : undefined,
        status: payment.status
    }));
};

const UserDetails: React.FC = () => {
    const { userId } = useParams<{ userId: string }>();
    const navigate = useNavigate();
    const [user, setUser] = useState<User | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [blockLoading, setBlockLoading] = useState<boolean>(false);
    const [paymentHistoryLoading, setPaymentHistoryLoading] = useState<boolean>(false);

    // Get auth token from localStorage
    const getAuthToken = () => {
        return localStorage.getItem('authToken') || 
               localStorage.getItem('token') || 
               localStorage.getItem('accessToken');
    };

    // Fetch user details from API
    const fetchUserDetails = async () => {
        if (!userId) {
            toast.error("User ID is required", {
                position: "top-right"
            });
            navigate("/user-management");
            return;
        }

        setLoading(true);
        try {
            const token = getAuthToken();
            const headers: HeadersInit = {
                'Content-Type': 'application/json',
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const response = await fetch(`${AppConstants.ADMIN_USER_MANAGEMENT_API}/${userId}`, {
                method: 'GET',
                headers
            });

            if (!response.ok) {
                if (response.status === 401) {
                    toast.error("Authentication required. Please log in again.", {
                        position: "top-right"
                    });
                    return;
                }
                if (response.status === 404) {
                    toast.error("User not found", {
                        position: "top-right"
                    });
                    navigate("/user-management");
                    return;
                }
                throw new Error(`Failed to fetch user details: ${response.status} ${response.statusText}`);
            }

            const data: APIUserDetailsResponse = await response.json();
            
            if (data.success) {
                const transformedUser = transformUserData(data.data);
                setUser(transformedUser);
            
            } else {
                toast.error(data.message || 'Failed to load user details', {
                    position: "top-right"
                });
                navigate("/user-management");
            }
        } catch (error) {
            toast.error("Failed to load user details. Please try again.", {
                position: "top-right"
            });
            navigate("/user-management");
        } finally {
            setLoading(false);
        }
    };

    // Fetch payment history from API
    const fetchPaymentHistory = async () => {
        if (!userId) {
            toast.error("User ID is required", {
                position: "top-right"
            });
            return;
        }

        setPaymentHistoryLoading(true);
        try {
            const token = getAuthToken();
            const headers: HeadersInit = {
                'Content-Type': 'application/json',
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const apiUrl = `${AppConstants.ADMIN_USER_MANAGEMENT_API}/${userId}/payment-history`;

            const response = await fetch(apiUrl, {
                method: 'GET',
                headers
            });


            if (!response.ok) {
                if (response.status === 401) {
                    console.error("Payment history: Authentication required");
                    toast.error("Authentication required. Please log in again.", {
                        position: "top-right"
                    });
                    return;
                }
                if (response.status === 404) {
                    // Don't show error toast for 404 on payment history as it's expected for some users
                    return;
                }
                const errorText = await response.text();
                console.error("Payment history error response:", errorText);
                throw new Error(`Failed to fetch payment history: ${response.status} ${response.statusText}`);
            }

            const data: APIPaymentHistoryResponse = await response.json();
            
            if (data.success) {
                const transformedPaymentHistory = transformPaymentHistoryData(data.data.payments);
                
                // Update user state with payment history
                setUser(prevUser => {
                    if (prevUser) {
                        return {
                            ...prevUser,
                            paymentHistory: transformedPaymentHistory
                        };
                    }
                    return prevUser;
                });
            } else {
                console.error("Payment history API returned success=false:", data.message);
                toast.error(data.message || 'Failed to load payment history', {
                    position: "top-right"
                });
            }
        } catch (error) {
            console.error("Error fetching payment history:", error);
            toast.error("Failed to load payment history. Please try again.", {
                position: "top-right"
            });
        } finally {
            setPaymentHistoryLoading(false);
        }
    };

    // Fixed handleBlockUser function
    const handleBlockUser = async (userId: string, isBlocked: boolean) => {
        setBlockLoading(true);
        try {
            const token = getAuthToken();
            const headers: HeadersInit = {
                'Content-Type': 'application/json',
            };

            if (token) {
                headers['Authorization'] = `Bearer ${token}`;
            }

            const requestBody = {
                action: isBlocked ? "block" : "unblock"
            };


            const response = await fetch(`${AppConstants.ADMIN_USER_MANAGEMENT_API}/${userId}/status`, {
                method: 'PATCH',
                headers,
                body: JSON.stringify(requestBody)
            });


            if (!response.ok) {
                const errorText = await response.text();
                
                if (response.status === 401) {
                    toast.error("Authentication required. Please log in again.", {
                        position: "top-right"
                    });
                    return;
                }
                if (response.status === 400) {
                    toast.error("Invalid request. Please check the request format.", {
                        position: "top-right"
                    });
                    return;
                }
                throw new Error(`Failed to update user status: ${response.status} ${response.statusText}`);
            }

            const data = await response.json();
            
            if (data.success) {
                // Update local state
                if (user) {
                    setUser({
                        ...user,
                        status: isBlocked ? 'Blocked' : 'Active'
                    });
                }
                toast.success(isBlocked ? "User blocked successfully" : "User unblocked successfully", {
                    position: "top-right"
                });
            } else {
                toast.error(data.message || 'Failed to update user status', {
                    position: "top-right"
                });
            }
        } catch (error) {
            console.error("Error updating user status:", error);
            toast.error("Failed to update user status. Please try again.", {
                position: "top-right"
            });
        } finally {
            setBlockLoading(false);
        }
    };

    useEffect(() => {
        fetchUserDetails();
    }, [userId, navigate]);

    return (
        <Container fluid className="p-0 user-details-page mb-5">
            <ToastContainer position="top-right" autoClose={3000} hideProgressBar={false} />
            
            {loading ? (
                <Loader text="Loading user details..." />
            ) : user ? (
                <UserDetailsComponent
                    user={user}
                    onBlockUser={handleBlockUser}
                    blockLoading={blockLoading}
                    onFetchPaymentHistory={fetchPaymentHistory}
                    paymentHistoryLoading={paymentHistoryLoading}
                />
            ) : (
                <div className="text-center py-5">
                    <h4>User not found</h4>
                    <p>The requested user could not be found.</p>
                    <Button variant="primary" onClick={() => navigate("/user-management")}>
                        Back to Users List
                    </Button>
                </div>
            )}
        </Container>
    );
};

export default UserDetails;